import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ReportConfigurationService } from 'app/infrastructure/services/reportConfiguration.service';
import { QueryBuilderConfig, RuleSet } from 'app/plugins/queryBuilder';
import {
  RuleBuilderStyles,
  EmptyRuleBuilder,
  RuleBuilderService,
} from 'app/infrastructure/services/ruleBuilder.service';

import { SortingCriteria } from 'app/infrastructure/model/enum/sortingCriteria.enum';
import {
  AlertService,
  GrowlType,
} from 'app/infrastructure/services/alert.service';
import { DocumentSubState } from 'app/infrastructure/model/enum/documentSubState.enum';
import { StandardDictionary } from 'app/infrastructure/model/interfaces/standarddictionary.interface';
import {
  AttributeCounter,
  BannedSortables,
  ReportConfigurationProduct,
  ReportConfiguration,
} from 'app/infrastructure/model/interfaces/reportConfiguration.interface';
import { OrganizationCategory } from 'app/infrastructure/model/interfaces/organizationCategory.interface';
import { DocumentState } from 'app/infrastructure/model/enum/documentState.enum';
import { ComparisonOperator } from 'app/infrastructure/model/enum/comparisonOperator.enum';
import { StandardDictionaryService } from 'app/infrastructure/services/standarddictionary.service';
import { SxTranslationService } from 'app/infrastructure/services/translation-service.service';

@Component({
  selector: 'sx-edit-report-configuration',
  templateUrl: './edit.report.configuration.component.html',
  styleUrls: ['./edit.report.configuration.component.css'],
})
export class EditReportConfigurationComponent implements OnInit {
  documentComponentMarker = 'Document-Component';
  allProductsMarker = 'All-Products';
  staticAttributes: string[] = ['DocumentId'];
  mandatoryAttributes: string[] = ['DocumentId', 'State', 'SubState'];

  systemCode: string;
  businessCode: string;
  reportCode: string;

  ruleBuilderStyle = RuleBuilderStyles;
  ruleBuilderConfig: QueryBuilderConfig = EmptyRuleBuilder;
  query: RuleSet = {
    condition: 'and',
    rules: [],
    type: 'RuleSet',
  };

  format = { 
    add: this.sxTranslationService.getResource('ADD_REPORT'), 
    remove: this.sxTranslationService.getResource('REMOVE_REPORT'), 
    all: this.sxTranslationService.getResource('ALL_REPORT'),
    none: this.sxTranslationService.getResource('NONE_REPORT'),
    draggable: true};
    
  report: ReportConfiguration;
  errores = {};
  isEdition = false;
  disableDocumentStateValue = true;
  disableDucomentStateCriteria = false;
  disableDocumentSubstateValue = true;
  disableDocumentSubstateCriteria = false;

  stateCustomValue = DocumentState.Custom;
  subStateCustomValue = DocumentSubState.Custom;
  comparisonOperatorAnyValue = ComparisonOperator.Any;

  documentState = DocumentState;
  documentSubstate = DocumentSubState;
  comparisonOperator = ComparisonOperator;
  sortingCriteria = SortingCriteria;
  availableProducts: string[];
  availableDictionaries: StandardDictionary[];
  selectedDictionaries: StandardDictionary[];
  products: ReportConfigurationProduct[];
  categories: OrganizationCategory[];
  targetDictionaries: string[] = [];

  attributes: string[] = [];
  orderByAttr: string[] = [];

  selectableColumns: string[] = [];

  constructor(
    private sxTranslationService: SxTranslationService,
    private aRoute: ActivatedRoute,
    private reportService: ReportConfigurationService,
    private standardDictionaryService: StandardDictionaryService,
    private ruleBuilderService: RuleBuilderService,
    private router: Router,
    private alertService: AlertService
  ) {}

  ngOnInit() {
    this.aRoute.params.subscribe((params: Params) => {
      this.systemCode = params['codsistema'];
      this.businessCode = params['codnegocio'];
      this.reportCode = params['codreporte'];

      this.selectedDictionaries = [];

      this.initializeReport();
      if(!this.systemCode || !this.businessCode) {
        this.reportService.getCommonDictionaries().subscribe((res) => {
          const commonDictionaries = res;
          this.standardDictionaryService.getAll().subscribe((res) => {
            this.availableDictionaries = commonDictionaries.concat(res);
          });
        });
  
        if (this.reportCode !== 'nuevo') {
          this.isEdition = true;
          this.reportService.get(this.reportCode).subscribe((r) => {
            this.report = r;
            this.reportService.getCategory().subscribe((res) => {
              this.categories = res;
            });
            if (r.subscriptions.length > 0) {
              this.systemCode = r.subscriptions[0].subscriptionCode;
              this.businessCode = r.subscriptions[0].channelCode;
            }
  
            if (r.standardDictionary != null) {
              if (r.standardDictionary.id != null) {
                this.selectedDictionaries.push(r.standardDictionary);
                this.reportService.getCommonDictionaries().subscribe((res) => {
                  const commonDictionaries = res;
                  this.standardDictionaryService.getAll().subscribe((res) => {
                    this.availableDictionaries = commonDictionaries.concat(res);
                    if (r.targetProducts.length > 0)
                      if (r.targetProducts.some((x) => x != ''))
                        for (const key in r.targetProducts) {
                          if (r.targetProducts[key] != '') {
                            const commonDict = this.availableDictionaries.find(
                              (dict) => dict.id == r.targetProducts[key]
                            );
                            this.selectedDictionaries.push(commonDict);
                          }
                        }
                    this.onStandardDictionaryChange();
                  });
                });
              }
            }
            this.updateSelectableColumns();
  
            if (!this.validateStandarDictionarySelectorVisibility())
              this.reportService
                .getProductsAttributes(this.systemCode, this.businessCode)
                .subscribe((products) => {
                  this.products = products;
                  this.availableProducts = [];
                  this.availableProducts.push(this.allProductsMarker);
                  products.forEach((pp) =>
                    this.availableProducts.push(pp.productCode)
                  );
                  this.getCommonAttributes(null);
                  this.onDocumentStateChange(this.report.documentState);
                  this.onDocumentSubstateChange(
                    this.report.documentSubState
                  );
                  this.onDocumentStateCriteriaChange(
                    this.report.documentStateCriteria
                  );
                  this.onDocumentSubStateCriteriaChange(
                    this.report.documentSubStateCriteria
                  );
  
                  // overwrite the query here
                  this.updateSelectableColumns();
                  this.disableDocumentStateValue =
                    this.report.documentState !== DocumentState.Custom.valueOf();
                  this.disableDocumentSubstateValue =
                    this.report.documentSubState !==
                    DocumentSubState.Custom.valueOf();
                  this.onProductChange();
  
                  this.ruleBuilderService
                    .getForBusinessReportConfiguration(
                      this.systemCode,
                      this.businessCode,
                      this.reportCode
                    )
                    .subscribe((config) => {
                      this.ruleBuilderConfig = {
                        fields: config.fields,
                        entities: config.entities,
                      };
                      this.query = config.ruleSet;
                    });
                });
          });
        } else {
          if (this.validateStandarDictionarySelectorVisibility()) {
            this.reportService.getCategory().subscribe((res) => {
              this.categories = res;
            });
            this.InitializeObligatoryAttributes();
          }
        }
      } else {
        this.reportService
        .getProductsAttributes(this.systemCode, this.businessCode)
        .subscribe(products => {
          this.products = products;
          this.availableProducts = [];
          this.availableProducts.push(this.allProductsMarker);
          products.forEach(pp => this.availableProducts.push(pp.productCode));
          this.getCommonAttributes(null);

          if (this.reportCode !== 'nuevo') {
            this.ruleBuilderService
              .getForBusinessReportConfiguration(
                this.systemCode,
                this.businessCode,
                this.reportCode
                ).subscribe(config => {
                this.ruleBuilderConfig =
                {
                  fields: config.fields,
                  entities: config.entities,
                };
                this.reportService
                  .getByContext(this.systemCode, this.businessCode, this.reportCode)
                  .subscribe(r => {
                    this.report = r;
                    // overwrite the query here
                    this.query = config.ruleSet;
                    this.updateSelectableColumns();
                    this.isEdition = true;
                    this.disableDocumentStateValue =
                      this.report.documentState !==
                      DocumentState.Custom.valueOf();
                    this.disableDocumentSubstateValue =
                      this.report.documentSubState !==
                      DocumentSubState.Custom.valueOf();
                    this.onProductChange();
                  });
              });
          } else {
            this.ruleBuilderService
              .getForBusinessReportConfiguration(
                this.systemCode,
                this.businessCode,
                null
              )
              .subscribe(config => {
                this.ruleBuilderConfig =
                {
                  fields: config.fields,
                  entities: config.entities,
                };
              });
            this.onDocumentStateChange(this.report.documentStateValue);
            this.onDocumentSubstateChange(this.report.documentSubStateValue);
            this.onDocumentStateCriteriaChange(
              this.report.documentStateCriteria
            );
            this.onDocumentSubStateCriteriaChange(
              this.report.documentSubStateCriteria
            );
          }
        });
      }
    });
  }

  isValidForm() {
    const regexInvalidReportCode = new RegExp('[^A-Z0-9]+');
    if (regexInvalidReportCode.test(this.report.reportCode)) {
      this.errores['attributeCode'] = this.sxTranslationService.getResource('INVALID_REPORT_CODE');
      return false;
    } else {
      this.errores['attributeCode'] = '';
    }

    if (
      this.report.documentStateCriteria != this.comparisonOperatorAnyValue &&
      this.report.documentState == this.stateCustomValue &&
      (this.report.documentStateValue === null ||
        this.report.documentStateValue.length === 0)
    ) {
      return false;
    }

    if (
      this.report.documentSubStateCriteria !==
        this.comparisonOperatorAnyValue &&
      this.report.documentSubState === this.subStateCustomValue &&
      (this.report.documentSubStateValue === null ||
        this.report.documentSubStateValue.length === 0)
    ) {
      return false;
    }
    if(!this.systemCode || !this.businessCode){
      if (this.isEdition) {
        if (!this.report.standardDictionary) {
          if (this.report.category.id == null) {
            return false;
          }
        }
        if (this.report.category.id != null && this.report.category.id != 0) {
          if (!this.report.standardDictionary) {
            return false;
          }
        }
      } else if (!this.report.category.id) {
  
        return false;
      } else if (!this.report.standardDictionary) {
  
        return false;
      } else if (!this.report.standardDictionary.id) {
        return false;
      }
      
      if (
        this.report.targetProducts.length == 0 &&
        this.targetDictionaries.length == 0
          ) {
            return false;
          }
    }
    else{
      if(this.report.targetProducts.length <= 0){
        return false;
      }
    }

    return (
      this.report.name.length > 0 &&
      this.report.reportCode.length > 0 &&
      this.report.orderBy != null &&
      this.report.orderBy !== '' &&
      this.report.orderByCriteria !== null &&
      this.report.documentState !== null &&
      this.report.documentStateCriteria !== null &&
      this.report.documentSubState !== null &&
      this.report.documentSubStateCriteria !== null &&
      this.report.columns.length
    );
  }

  save() {
    if (this.isValidForm()) {
      this.report.reportRuleSet.builder = JSON.stringify(this.query);
      this.report.reportRuleSet.fields = JSON.stringify(
        this.ruleBuilderConfig.fields
      );

      this.report.reportRuleSet.status = 'OK';
      this.reportService.save(this.report).then((r) => {
        this.alertService.showGrowl(
          {
            type: GrowlType.SUCCESS,
            body: 'REPORT_CONFIGURATION_SUCCESS',
          },
          { code: r.reportCode }
        );

        this.back();
      });
    } else {
      this.alertService.showGrowl({
        type: GrowlType.WARNING,
        body: 'REPORT_CONFIGURATION_WARING_VALID_FORM',
      });
    }
  }

  disable() {
    this.reportService
      .delete(this.systemCode, this.businessCode, this.reportCode)
      .then((success) => {
        if (success) {
          this.alertService.showGrowl(
            {
              type: GrowlType.SUCCESS,
              body: 'REPORT_CONFIGURATION_DELETE_SUCCESS',
            },
            { code: this.reportCode }
          );
          this.back();
        } else {
          this.alertService.showGrowl(
            {
              type: GrowlType.DANGER,
              body:
                'SOC_FE_0013_COULD_NOT_DELETE_REPORT_CONFIGURATION_WITH_CODE',
            },
            { code: this.reportCode }
          );
        }
      });
  }

  updateSelectableColumns() {
    const selectedColumns: string[] = [];
    this.selectableColumns = [];
    this.report.columns.forEach((element) => {
      if (!BannedSortables.find((ban) => ban === element)) {
        selectedColumns.push(element);
        this.selectableColumns = selectedColumns;
      }
    });
    if(!this.report.columns.some(x => this.selectableColumns.some(y => y == x))) {
      this.report.orderBy = null;
    }

    if(this.validateStandarDictionarySelectorVisibility()){
      this.report.columns = this.staticAttributes.concat(selectedColumns);
      this.selectableColumns = this.staticAttributes.concat(selectedColumns);
    }
  }

  onStandardDictionaryChange() {
    this.attributes = [];

    // Make a copy of selected dictionaries to handle it with out affect the original.
    let standardDictionariesArray = [...this.selectedDictionaries];
    const arrayLength = this.selectedDictionaries.length;

    //Clean Target Products
    this.report.targetProducts = [];

    // Here we validate that its there only 1 standardDictionary with the exception of Document Component Marker.
    if (arrayLength > 1) {
      for (const key in standardDictionariesArray) {
        this.targetDictionaries.push(standardDictionariesArray[key].id);
      }
      if (arrayLength >= 3) {
        if (
          standardDictionariesArray.some(
            (x) => x.id == this.documentComponentMarker
          )
        )
          for (const key in standardDictionariesArray) {
            if (
              standardDictionariesArray[key].id !== this.documentComponentMarker
            ) {
              standardDictionariesArray.splice(parseInt(key), 1);
              break;
            }
          }
      } else {
        if (
          !standardDictionariesArray.some(
            (x) => x.id == this.documentComponentMarker
          )
        ) {
          standardDictionariesArray.shift();
        }
      }
    }
    this.selectedDictionaries = standardDictionariesArray;
    this.targetDictionaries = standardDictionariesArray.map((x) => x.id);

    // Use the target product propertie to persist the document component marker in the standard dictionary assignations.
    if (
      standardDictionariesArray.some(
        (sd) => sd.id == this.documentComponentMarker
      )
    ) {
      const documentComponentDictionary = standardDictionariesArray.find(
        (sd) => sd.id == this.documentComponentMarker
      );
      this.report.targetProducts.push(documentComponentDictionary.id);
    }

    //Standard Dictionary asignation if there is one.
    this.report.standardDictionary = this.selectedDictionaries.some(
      (sd) => sd.id !== this.documentComponentMarker
    )
      ? this.selectedDictionaries.find(
          (sd) => sd.id !== this.documentComponentMarker
        )
      : null;

    // Only if there is some standardDictionary to find its attributes.
    if (arrayLength !== 0) {
      this.reportService
        .getStandardAttributes(this.selectedDictionaries)
        .subscribe((att) => {
          const attributesToAdd: string[] = [];

          for (const key in att) {
            if (!this.attributes.some((attribute) => attribute == att[key])) {
              attributesToAdd.push(att[key]);
            }
          }
          this.attributes = this.mandatoryAttributes.concat(attributesToAdd);
        });
    }
  }

  onProductChange() {
    this.getCommonAttributes(this.report.targetProducts);
    this.matchCommonsWithCurrents();
  }

  //#region Attribute matching/gather
  matchCommonsWithCurrents() {
    const matched: string[] = [];
    this.report.columns.forEach((x) => {
      const match = this.attributes.find((att) => att === x);
      if (match) {
        matched.push(x);
      }
    });
    this.report.columns = matched;
    // mathces the orderby
    if (!this.attributes.find((att) => att === this.report.orderBy)) {
      this.report.orderBy = '';
    }
    return;
  }

  getAttributesByProductGroup(productCodes: string[] | null): string[] {
    const ret: string[] = [];
    if (productCodes === null) {
      this.products.forEach((p) => {
        if (p.productCode !== this.documentComponentMarker) {
          p.attributes.forEach((a) => {
            ret.push(a);
          });
        }
      });
    } else {
      // executes once
      productCodes.forEach((element) => {
        const matchProduct = this.products.find(
          (x) => x.productCode === element
        );
        if (matchProduct) {
          matchProduct.attributes.forEach((attr) => {
            ret.push(attr);
          });
        }
      });
    }
    return ret;
  }

  getCommonAttributes(productCodes: string[] | null) {
    this.attributes = [];
    const attrs: string[] = this.getAttributesByProductGroup(productCodes);

    let targetCount = 0;
    let areComps = false;

    if (productCodes !== null) {
      targetCount = productCodes.length;
      // if there is the marker,
      if (productCodes.find((x) => x === this.documentComponentMarker)) {
        areComps = true;
        targetCount = targetCount - 1;
      }
    } else {
      // executes once
      // to account the all and the document component
      targetCount = this.products.length - 1;
    }

    const uniqueCounters = this.getUniqueCounters(attrs);
    const commonAttributes: string[] = [];
    uniqueCounters.forEach((count) => {
      if (count.count >= targetCount) {
        commonAttributes.push(count.attribute);
      }
    });

    if (areComps) {
      const compsProd = this.products.find(
        (x) => x.productCode === this.documentComponentMarker
      );
      compsProd.attributes.forEach((element) => {
        commonAttributes.push(element);
      });
    }

    commonAttributes.sort();
    this.attributes = commonAttributes;
    if (productCodes === null) {
      const allProducts: ReportConfigurationProduct = {
        systemCode: this.systemCode,
        businessCode: this.businessCode,
        productCode: this.allProductsMarker,
        description: this.allProductsMarker,
        attributes: commonAttributes,
      };
      this.products.push(allProducts);
    }
    return;
  }

  getUniqueCounters(unfiltered: string[]): AttributeCounter[] {
    const uniqueArray: AttributeCounter[] = [];
    unfiltered.forEach((element) => {
      const mathcCounter = uniqueArray.find((x) => x.attribute === element);
      if (!mathcCounter) {
        const counter: AttributeCounter = {
          attribute: element,
          count: 1,
        };
        uniqueArray.push(counter);
      } else {
        mathcCounter.count++;
      }
    });
    return uniqueArray;
  }
  //#endregion

  //#region DocumentPrefilters
  onDocumentStateChange(state) {
    if (state != this.stateCustomValue) {
      this.report.documentStateValue = null;
      this.disableDocumentStateValue = true;
    } else {
      this.disableDocumentStateValue = false;
    }
  }

  onDocumentSubstateChange(substate) {
    if (substate != this.subStateCustomValue) {
      this.report.documentSubStateValue = null;
      this.disableDocumentSubstateValue = true;
    } else {
      this.disableDocumentSubstateValue = false;
    }
  }

  onDocumentStateCriteriaChange(state) {
    if (state == this.comparisonOperatorAnyValue) {
      this.report.documentStateValue = null;
      this.disableDucomentStateCriteria = true;
      this.disableDocumentStateValue = true;
    } else {
      this.disableDucomentStateCriteria = false;
      if (this.report.documentState == this.stateCustomValue) {
        this.disableDocumentStateValue = false;
      } else {
        this.disableDocumentStateValue = true;
      }
    }
  }

  onDocumentSubStateCriteriaChange(state) {
    if (state == this.comparisonOperatorAnyValue) {
      this.report.documentSubStateValue = null;
      this.disableDocumentSubstateCriteria = true;
      this.disableDocumentSubstateValue = true;
    } else {
      this.disableDocumentSubstateCriteria = false;
      if (this.report.documentSubState == this.subStateCustomValue) {
        this.disableDocumentSubstateValue = false;
      } else {
        this.disableDocumentSubstateValue = true;
      }
    }
  }
  //#endregion

  //#region navigation
  initializeReport() {
    this.report = {
      id: '',
      reportCode: '',
      systemCode: this.systemCode,
      businessCode: this.businessCode,
      status: 'A',
      isExternal: false,
      name: '',
      orderBy: '',
      targetProducts: (!this.systemCode && !this.businessCode) ? [] : ['All-Products'],
      documentSubState: DocumentSubState.Quote.valueOf(),
      documentState: DocumentState.Quote.valueOf(),
      documentStateCriteria: ComparisonOperator.Equal.valueOf(),
      documentSubStateCriteria: ComparisonOperator.Equal.valueOf(),
      orderByCriteria: SortingCriteria.Asc.valueOf(),
      columns: [],
      category: {
        id: null,
        codOrganization: null,
        description: null,
        status: null,
        isSelected: false,
      },
      subscriptions: [],
      reportRuleSet: {
        builderId: null,
        ruleId: null,
        status: 'OK',
        builder: '',
        fields: '',
      },
      standardDictionary: {
        id: null,
        name: null,
        description: null,
        jsonFile: null,
        category: null,
        categories: [],
      },
    };
  }

  OnCategoryChange(categoryId: number) {
    const cat = this.categories.find((category) => category.id == categoryId);
    this.report.category = cat;
  }

  CategoryIsSelected(categoryId: any): boolean {
    return this.report.category.id === categoryId;
  }

  validateCategorySelectorVisibility() {
    let response: boolean = true;
    if (this.isEdition) {
      if (!this.report.category || this.businessCode || this.systemCode) {
        response = false;
      } else {
        if (!this.report.category.id) response = false;
      }
    }
    else if (this.businessCode || this.systemCode) {
        response = false;
    }
    return response;
  }

  validateStandarDictionarySelectorVisibility() {
    let response: boolean = true;

    if (this.isEdition) {
      if (!this.report.standardDictionary || this.businessCode || this.systemCode) {
        response = false;
      } else {
        if (!this.report.standardDictionary.id) response = false;
      }
    }
    else if (this.businessCode || this.systemCode){
      response = false;
    }
    return response;
  }

  goToMapping() {
    if (this.isValidForm()) {
      this.report.reportRuleSet.builder = JSON.stringify(this.query);
      this.report.reportRuleSet.status = 'OK';
      this.reportService.save(this.report).then((r) => {
        this.alertService.showGrowl(
          {
            type: GrowlType.SUCCESS,
            body: 'REPORT_CONFIGURATION_SUCCESS',
          },
          { code: r.reportCode }
        );
        this.router.navigate(['mapping'], { relativeTo: this.aRoute });
      });
    } else {
      this.alertService.showGrowl({
        type: GrowlType.WARNING,
        body: 'REPORT_CONFIGURATION_WARING_VALID_FORM',
      });
    }
  }

  rulePath(ruleIdFactory: (config: ReportConfiguration) => number) {
    const ruleId = ruleIdFactory(this.report);

    // check si no tiene regla asociada la genero.
    if (ruleId == null) {
      if (this.isValidForm()) {
        this.reportService.save(this.report).then((r) => {
          this.reportService.get(this.reportCode).subscribe((c) => {
            this.router.navigate(['confRegla', ruleIdFactory(c)], {
              relativeTo: this.aRoute,
            });
          });
        });
      }
    } else {
      this.router.navigate(['confRegla', ruleId], { relativeTo: this.aRoute });
    }
  }

  openQueryListRule(): void {
    const queryListRuleIdFactory = (config: ReportConfiguration) =>
      config.reportRuleSet.ruleId;

    this.rulePath(queryListRuleIdFactory);
  }

  openDetailRulePath(): void {
    const openDetailRuleIdFactory = (config: ReportConfiguration) =>
      config.reportRuleSet.openDetailRuleId;

    this.rulePath(openDetailRuleIdFactory);
  }

  back() {
    this.router.navigate(['../'], { relativeTo: this.aRoute });
  }

  InitializeObligatoryAttributes() {
    this.attributes = [...this.mandatoryAttributes];
    this.report.columns = [...this.mandatoryAttributes];
  }

  //#endregion
}



// WEBPACK FOOTER //
// D:/a/1/s/BackOffice/SOC/SxSoc.ClientWeb/src/app/private/reportConfiguration/edit.report.configuration.component.ts